import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid2,
  Container,
  Typography,
  Paper,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import handleAxiosError from "../../components/ErrorHandler";
import { notifErr, notifOk } from "../../components/Functions";
import {
  TrainingSchedule,
  trainingScheduleInit,
} from "../../model/entity/TrainingSchedule";
import {
  trainingScheduleValidationObjInit,
  validateTrainingSchedule,
} from "../../model/validation/TrainingScheduleValidation";
import { Loading } from "../../components/Loading";
import { getFetcher, postFetcher } from "../../components/Fetchers";
import LoadingDialog from "../../components/LoadingDialog";

const CopySchedule = () => {
  const navigate = useNavigate();
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [activeSchedule, setActiveSchedule] = useState<TrainingSchedule | null>(
    null
  );
  const [newSchedule, setNewSchedule] =
    useState<TrainingSchedule>(trainingScheduleInit);
  const [scheduleErr, setScheduleErr] = useState(
    trainingScheduleValidationObjInit
  );
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getFetcher(
          `${process.env.REACT_APP_API_URL}/schedules/active`
        );
        setActiveSchedule(response.data);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (triedSubmit) setScheduleErr(validateTrainingSchedule(newSchedule));
  }, [newSchedule, triedSubmit]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewSchedule({ ...newSchedule, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTriedSubmit(true);
    if (!newSchedule) return;

    const scheduleErrors = validateTrainingSchedule(newSchedule);
    setScheduleErr(scheduleErrors);

    if (Object.values(scheduleErrors).some((val) => val === false)) return;

    try {
      setProcessing(true);
      await postFetcher(
        `${process.env.REACT_APP_API_URL}/schedules/copy`,
        newSchedule
      );
      notifOk("Kopie úspěšně vytvořena.");
      navigate("/schedule/settings");
    } catch (err) {
      notifErr(handleAxiosError(err));
    } finally {
      setProcessing(false);
    }
  };

  if (activeSchedule === null) return <Loading />;

  const loadingDialogTitle = "Probíhá vytváření kopie...";
  const loadingDialogMsg =
    "Kopírování rozvrhu na celé pololetí může trvat i několik desítek sekund.";

  return (
    <Container
      maxWidth="sm"
      sx={{
        pb: "2rem",
      }}
    >
      <LoadingDialog open={processing} title={loadingDialogTitle} msg={loadingDialogMsg} />
      <Paper elevation={3} sx={{ padding: "1.5rem" }}>
        <Typography variant="h5" sx={{ marginBottom: "0.5rem" }}>
          Kopie rozvrhu <b>{activeSchedule.name}</b>
        </Typography>
        <Typography
          variant="body1"
          sx={{ marginBottom: "2rem", textAlign: "justify" }}
        >
          Vytvořením kopie se do nového rozvrhu přenesou veškeré kurzy
          s&nbsp;časovými sloty, přiřazenými trenéry a přihlášenými členy.
          Manuální&nbsp;úpravy (upravené výše příspěvků, záskoky, zrušené dny)
          se do nové kopie nepřenáší.
        </Typography>
        <Grid2 container spacing={3} size={{ xs: 12, md: 6 }}>
          <Grid2
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <TextField
              size="small"
              label="Název nového rozvrhu"
              required
              name="name"
              value={newSchedule.name}
              error={!scheduleErr.name}
              onChange={handleInputChange}
              sx={{ width: "50%" }}
            />
          </Grid2>
          <Grid2
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD.MM.YYYY"
                label="Datum zahájení"
                sx={{ width: "50%" }}
                name="startDate"
                value={
                  newSchedule.startDate === ""
                    ? null
                    : dayjs(newSchedule.startDate)
                }
                onChange={(date) =>
                  setNewSchedule({
                    ...newSchedule,
                    startDate: date ? date.format("YYYY-MM-DD") : "",
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                    error:
                      !scheduleErr.startDate &&
                      !dayjs(
                        newSchedule.startDate,
                        "YYYY-MM-DD",
                        true
                      ).isValid(),
                  },
                  field: { clearable: true },
                }}
              />
            </LocalizationProvider>
          </Grid2>
          <Grid2
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD.MM.YYYY"
                label="Datum ukončení"
                sx={{ width: "50%" }}
                name="endDate"
                value={
                  newSchedule.endDate === "" ? null : dayjs(newSchedule.endDate)
                }
                onChange={(date) =>
                  setNewSchedule({
                    ...newSchedule,
                    endDate: date ? date.format("YYYY-MM-DD") : "",
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                    error:
                      !scheduleErr.endDate &&
                      !dayjs(newSchedule.endDate, "YYYY-MM-DD", true).isValid(),
                  },
                  field: { clearable: true },
                }}
              />
            </LocalizationProvider>
          </Grid2>

          <Grid2
            container
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "right", mt: 1, gap: 2 }}
            id="top"
          >
            <Button
              className="cancelBtn"
              variant="outlined"
              onClick={() => navigate("/schedule/settings")}
            >
              Zrušit
            </Button>
            <Button
              className="submitBtn"
              variant="outlined"
              onClick={handleSubmit}
            >
              Vytvořit
            </Button>
          </Grid2>
        </Grid2>
      </Paper>
    </Container>
  );
};

export default CopySchedule;
